import { zodResolver } from '@hookform/resolvers/zod';
import { CargoType, HandlingOptions } from '@pelicargo/types';
import { HANDLING_OPTIONS, PACKING_OPTIONS } from '@pelicargo/ui';
import { getDifferences } from '@pelicargo/utils';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { TrpcInputs, TrpcOutputs } from '../../config/trpc';

export type CargoInput = NonNullable<TrpcInputs['createCargo']>;
export type CargoOutput = TrpcOutputs['createCargo'];

type Options = { isCreate: boolean };

const defaultOptions: Options = { isCreate: false };

const formatCargo = (data: any) => ({
  ...data,
  handling: HANDLING_OPTIONS.find((option) => data?.handling.includes(option.value))?.value || '',
  packing: data?.handling
    .filter((value) => PACKING_OPTIONS.some((option) => option.value === value))
    .map((value) => value),
});

const validationSchema = z
  .object({
    quantity: z.coerce.number(),
    type: z.nativeEnum(CargoType),
    handling: z.nativeEnum(HandlingOptions),
    packing: z.nativeEnum(HandlingOptions).array(),

    length: z.coerce
      .number({ invalid_type_error: 'Length must be a number' })
      .refine((val) => /^\d*.?\d{0,2}$/.test(val.toString()), {
        message: 'Length cannot have more than two decimal places',
      }),

    width: z.coerce
      .number({ invalid_type_error: 'Width must be a number' })
      .refine((val) => /^\d*.?\d{0,2}$/.test(val.toString()), {
        message: 'Width cannot have more than two decimal places',
      }),

    height: z.coerce
      .number({ invalid_type_error: 'Height must be a number' })
      .refine((val) => /^\d*.?\d{0,2}$/.test(val.toString()), {
        message: 'Height cannot have more than two decimal places',
      }),

    weight: z.coerce.number({ invalid_type_error: 'Weight must be a number' }),
  })
  .nullish();

type FormValues = CargoInput & {
  handling?: HandlingOptions;
  packing?: HandlingOptions[];
};

export const useCargoForm = (data?: FormValues, { isCreate }: Options = defaultOptions) => {
  const { reset, getValues, setValue, ...methods } = useForm<Partial<FormValues>>({
    mode: 'onBlur',
    resolver: zodResolver(validationSchema),
    defaultValues: formatCargo(data),
  });

  useEffect(() => {
    const updateFields = () => {
      const changes = getDifferences<FormValues>(getValues(), data);
      if (!changes.length) return;

      for (const [key, value] of changes) {
        if (key === 'handling' && value) {
          const nextHandling = HANDLING_OPTIONS.find((option) => value?.includes(option.value))?.value || '';
          setValue('handling', nextHandling as HandlingOptions);
        } else if (key === 'packing' && value) {
          const nextPacking = value
            .filter((value) => PACKING_OPTIONS.some((option) => option.value === value))
            .map((value) => value);
          setValue('packing', nextPacking);
        } else if (value !== undefined) setValue<any>(key, value);
      }
    };

    // Run if we're not on create & we have data
    if (!isCreate && data) updateFields();
  }, [data, reset, getValues, setValue, isCreate]);

  return { methods: { ...methods, getValues, setValue, reset } };
};
